import React from 'react';
import PropTypes from 'prop-types';
import { Ocean, Segment, Segments, ThinkalikeModel, styles } from '@pinpoint/onpoint-charts';
import { Div, Table } from '@pinpoint/design-kit';

import CONSTANTS from 'src/store/constants';
import { ModelProp } from 'src/store/typings';
import Message from './message';

export function ApiWrapper({ hasData, getData, apiStatus, children }) {
  if (hasData) return children;
  if (apiStatus === CONSTANTS.API.STARTED) return <Message />;
  if (apiStatus === CONSTANTS.API.FAILED) return <Message type="error" />;
  return <Message type="info" text="No data" cta={{ text: 'Refresh', onClick: getData }} />;
}

ApiWrapper.propTypes = {
  getData: PropTypes.func.isRequired,
  hasData: PropTypes.bool.isRequired,
  apiStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
    .isRequired,
};

export function PredictiveModelWrapper({ getData, hasData, apiStatus, model }) {
  return hasData ? (
    <ThinkalikeModel
      totalUsers={model.lift_scores.total_users}
      engagedUsers={model.lift_scores.engaged_users}
      percentile={model.lift_scores.percentile}
      lift={model.lift_scores.lift}
      copyOverride={{}}
    />
  ) : apiStatus === CONSTANTS.API.STARTED ? (
    <Message />
  ) : apiStatus === CONSTANTS.API.FAILED ? (
    <Message type="error" />
  ) : apiStatus === CONSTANTS.API.SUCCEEDED ? (
    <Message
      type="info"
      cta={{
        text: 'Refresh',
        onClick: () => {
          getData();
        },
      }}
    />
  ) : (
    <Message
      type="info"
      text="No data to display. Please select dataset. "
      cta={{
        text: 'Refresh',
        onClick: () => {
          getData();
        },
      }}
    />
  );
}

PredictiveModelWrapper.propTypes = {
  getData: PropTypes.func.isRequired,
  hasData: PropTypes.bool.isRequired,
  model: ModelProp.isRequired,
  apiStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
    .isRequired,
};

export function SegmentViewWrapper({ getData, hasData, data, apiStatus, ...rest }) {
  return hasData ? (
    <Segment
      heading=""
      object={data}
      style={styles.Style1}
      styleOverride={{
        background: '#FFF',
        overflow: 'auto',
      }}
      minH="100%"
      minW="100%"
      {...rest}
    />
  ) : apiStatus === CONSTANTS.API.STARTED ? (
    <Message />
  ) : apiStatus === CONSTANTS.API.FAILED ? (
    <Message type="error" />
  ) : apiStatus === CONSTANTS.API.SUCCEEDED ? (
    <Message
      type="info"
      cta={{
        text: 'Refresh',
        onClick: getData,
      }}
    />
  ) : (
    <Message
      type="info"
      text="No data"
      cta={{
        text: 'Refresh',
        onClick: getData,
      }}
    />
  );
}

SegmentViewWrapper.propTypes = {
  getData: PropTypes.func.isRequired,
  hasData: PropTypes.bool.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  apiStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
    .isRequired,
};

export function SegmentsViewWrapper({ heading, getData, hasData, data, apiStatus, variant, ...rest }) {
  return hasData ? (
    <Segments
      heading={heading}
      objects={data}
      style={styles.Style1}
      maxH="100%"
      maxW="100%"
      minH="100%"
      minW="100%"
      variant={variant}
      {...rest}
    />
  ) : apiStatus === CONSTANTS.API.STARTED ? (
    <Message />
  ) : apiStatus === CONSTANTS.API.FAILED ? (
    <Message type="error" />
  ) : apiStatus === CONSTANTS.API.SUCCEEDED ? (
    <Message
      type="info"
      cta={{
        text: 'Refresh',
        onClick: () => {
          getData();
        },
      }}
    />
  ) : (
    <Message
      type="info"
      text="No data"
      cta={{
        text: 'Refresh',
        onClick: () => {
          getData();
        },
      }}
    />
  );
}

SegmentsViewWrapper.propTypes = {
  heading: PropTypes.string,
  getData: PropTypes.func.isRequired,
  hasData: PropTypes.bool.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  apiStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
    .isRequired,
  variant: PropTypes.string,
};

SegmentsViewWrapper.defaultProps = {
  heading: undefined,
  variant: undefined,
};

export function OceanWrapper({ getData, hasData, data, apiStatus }) {
  return hasData ? (
    <Ocean objects={[data]} style={styles.Style1} minH="100%" minW="100%" />
  ) : apiStatus === CONSTANTS.API.STARTED ? (
    <Message />
  ) : apiStatus === CONSTANTS.API.FAILED ? (
    <Message type="error" />
  ) : apiStatus === CONSTANTS.API.SUCCEEDED ? (
    <Message
      type="info"
      cta={{
        text: 'Refresh',
        onClick: () => {
          getData();
        },
      }}
    />
  ) : (
    <Message
      type="info"
      text="No data"
      cta={{
        text: 'Refresh',
        onClick: () => {
          getData();
        },
      }}
    />
  );
}

OceanWrapper.propTypes = {
  getData: PropTypes.func.isRequired,
  hasData: PropTypes.bool.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  apiStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
    .isRequired,
};

export function GridWrapper({
  apiStatus,
  columns,
  controlledGridState,
  data,
  enableSelection,
  getData,
  gridState,
  hasData,
  hasGlobalFilter,
  hasSelection,
  maxSelection,
  onRowClick,
  onRowDeselect,
  onRowExpand,
  onRowSelect,
  paginate,
  rawData,
  selectionTooltip,
  setGridState,
  subRowsColsIndentIndexes,
  widths,
  ...props
}) {
  if (hasData)
    return (
      <Div m={{ y: '1.25rem' }} p={{ x: '1.5rem' }} bg="white" rounded="md" {...props.DivProps}>
        <Table
          columns={columns}
          data={data}
          gridState={gridState}
          controlledGridState={controlledGridState}
          hasGlobalFilter={hasGlobalFilter}
          onRowClick={onRowClick}
          hasSelection={hasSelection}
          maxSelection={maxSelection}
          enableSelection={enableSelection}
          selectionTooltip={selectionTooltip}
          onRowSelect={onRowSelect}
          onRowDeselect={onRowDeselect}
          setGridState={setGridState}
          subRowsColsIndentIndexes={subRowsColsIndentIndexes}
          paginate={paginate}
          widths={widths}
          onRowExpand={onRowExpand}
          {...props.TableProps}
        />
      </Div>
    );
  if (apiStatus === CONSTANTS.API.STARTED || apiStatus === undefined) return <Message />;
  if (apiStatus === CONSTANTS.API.FAILED) return <Message type="error" />;
  if (apiStatus === CONSTANTS.API.SUCCEEDED && (!!data?.length || !!rawData?.length))
    return <Message type="info" text="Data is not viewable" cta={{ text: 'Refresh', onClick: getData }} />;
  return <Message type="info" text="No data" cta={{ text: 'Refresh', onClick: getData }} />;
}

GridWrapper.propTypes = {
  getData: PropTypes.func.isRequired,
  hasData: PropTypes.bool.isRequired,
  gridState: PropTypes.shape({}),
  controlledGridState: PropTypes.shape({}),
  hasGlobalFilter: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rawData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onRowClick: PropTypes.func,
  enableSelection: PropTypes.func,
  selectionTooltip: PropTypes.func,
  onRowSelect: PropTypes.func,
  onRowDeselect: PropTypes.func,
  onRowExpand: PropTypes.func,
  hasSelection: PropTypes.bool,
  maxSelection: PropTypes.number,
  apiStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
    .isRequired,
  setGridState: PropTypes.func,
  subRowsColsIndentIndexes: PropTypes.arrayOf(PropTypes.number),
  paginate: PropTypes.bool,
  widths: PropTypes.string,
  DivProps: PropTypes.shape({}),
  TableProps: PropTypes.shape({}),
};

GridWrapper.defaultProps = {
  gridState: {},
  controlledGridState: {},
  onRowClick: () => null,
  enableSelection: () => true,
  selectionTooltip: () => null,
  onRowSelect: () => null,
  onRowDeselect: () => null,
  onRowExpand: () => null,
  hasGlobalFilter: false,
  hasSelection: false,
  maxSelection: 3,
  setGridState: () => null,
  subRowsColsIndentIndexes: [],
  paginate: null,
  widths: null,
  DivProps: {},
  TableProps: {},
};
